import { useNavigate, useLocation } from 'react-router-dom';
import { useAppContext } from '../../Context';
import { useAppDispatch, useAppSelector } from '../../hooks';
import { setCustomers, updateCustomer, setCurrentPage, setRowsPerPage } from '../../slices/customersSlice';
import { CustomerDuplicateType, CustomerType } from '../../types';
import {
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControlLabel,
  Grid,
  Switch,
  TextField,
} from '@mui/material';
import DataTable from '../../components/shared/DataTable';
import { usePermissions } from '../../services';
import { useEffect, useState } from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faEye, faRefresh, faSync } from '@fortawesome/free-solid-svg-icons';
import { setLoading, setPopAlert } from '../../slices/miscSlice';
import { post } from '../../agent';
import { Button } from '../../components/shared/FormElements';

const ViewCustomersList: React.FC = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const dispatch = useAppDispatch();
  const { apiCall } = useAppContext();

  const { canView_customer, canView_menu_customers, canAdd_customer, canView_city, canAdd_city } = usePermissions([
    'view_customer',
    'view_menu_customers',
    'add_customer',
    'view_city',
    'add_city',
  ]);

  const customers: Array<CustomerType> = useAppSelector((state) => state.customer.customers);
  const currentPage: number = useAppSelector((state) => state.customer.currentPage);
  const rowsPerPage: number = useAppSelector((state) => state.customer.rowsPerPage);
  const [siigoConfigured, setSiigoConfigured] = useState(false);
  const [alegraConfigured, setAlegraConfigured] = useState(false);
  const [duplicates, setDuplicates] = useState<Array<CustomerDuplicateType>>([]);
  const [showDuplicates, setShowDuplicates] = useState(false);
  const [selectedDuplicateKeys, setSelectedDuplicateKeys] = useState<Record<string, boolean>>({});
  const FIELD_LABELS: Record<string, string> = {
    customer_name: 'Nombre',
    identification: 'Identificación',
    email: 'Correo',
    phone: 'Teléfono/Celular',
  };
  const resolveAlegraDuplicate = async (payload: {
    customer_id: number;
    alegra_id: string | number;
    updates?: Record<string, any>;
  }) => {
    try {
      const rawResp = await post('customers/resolve-alegra-duplicate/', {
        ...payload,
        updates: payload.updates || {},
      });
      let data: any = null;
      try {
        data = await rawResp.json();
      } catch (error) {
        data = null;
      }
      const ok = rawResp.status === 200 || rawResp.status === 201;
      return {
        ok,
        message: data?.detail,
        data,
      };
    } catch (error) {
      return {
        ok: false,
        message: 'No se pudo actualizar el cliente',
        data: null,
      };
    }
  };
  const [showManualModal, setShowManualModal] = useState(false);
  const [showCityModal, setShowCityModal] = useState(false);
  const [cities, setCities] = useState<Array<any>>([]);
  const [manualForm, setManualForm] = useState({
    firstName: '',
    lastName: '',
    identification: '',
    idTypeCode: '13',
    idTypeName: 'Cédula de ciudadanía',
    personType: 'Person',
    checkDigit: '',
    commercialName: '',
    branchOffice: '0',
    active: true,
    vatResponsible: false,
    address: '',
    cityId: '',
    phone: '',
    phoneIndicative: '',
    phoneExtension: '',
    email: '',
    contactFirstName: '',
    contactLastName: '',
    contactPhone: '',
    contactIndicative: '',
    fiscalCode: 'R-99-PN',
    fiscalName: 'No aplica - Otros',
  });
  const [cityForm, setCityForm] = useState({
    city_name: '',
    state_name: '',
    country_name: '',
  });
  useEffect(() => {
    if (!canView_customer && !canView_menu_customers) {
      navigate('/');
      return;
    }

    // Leer página de los query parameters si existe
    const searchParams = new URLSearchParams(location.search);
    const pageFromUrl = searchParams.get('page');
    const rowsFromUrl = searchParams.get('rows');

    if (pageFromUrl && !isNaN(Number(pageFromUrl))) {
      dispatch(setCurrentPage(Number(pageFromUrl)));
    }
    if (rowsFromUrl && !isNaN(Number(rowsFromUrl))) {
      dispatch(setRowsPerPage(Number(rowsFromUrl)));
    }

    getCustomers();
    getSiigoIntegration();
    if (canView_city) {
      getCities();
    }
    getAlegraIntegration();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const getCustomers = async () => {
    dispatch(setLoading(true));
    const response = await apiCall('GET', 'customers/full/', null, '', 'No se pudo obtener los clientes', true);
    dispatch(setLoading(false));
    if (response) {
      dispatch(setCustomers(response));
    }
  };

  const getSiigoIntegration = async () => {
    const response = await apiCall('GET', 'siigo-integration/', null, '', 'Error al obtener Siigo');
    setSiigoConfigured(!!response?.configured);
  };

  const getAlegraIntegration = async () => {
    const response = await apiCall('GET', 'alegra-integration/', null, '', 'Error al obtener Alegra');
    setAlegraConfigured(!!response?.configured);
  };

  const getCities = async () => {
    const response = await apiCall('GET', 'cities/', null, '', 'No se pudo obtener las ciudades');
    if (response) {
      setCities(response);
    }
  };

  const handleGetCustomer = async (id: string, source?: 'siigo' | 'alegra') => {
    const sourceQuery = source ? `?source=${source}` : '';
    const response = await apiCall('GET', `customers/${id}/${sourceQuery}`, null, '', 'No se pudo obtener el cliente', true);
    if (response) {
      dispatch(updateCustomer(response));
      const updatedCustomers = customers.map((customer) => {
        if (customer.id === Number(id)) {
          return {
            ...customer,
            is_synced: response.synced ?? customer.is_synced,
            last_synced_at: response.last_synced_at ?? customer.last_synced_at,
          };
        }
        return customer;
      });
      dispatch(setCustomers(updatedCustomers));
    }
  };

  const handleRefreshClients = async () => {
    dispatch(setLoading(true));
    const response = await apiCall('GET', 'customers/refresh/', null, '', 'Error al obtener los clientes');
    dispatch(setLoading(false));
    if (response) {
      dispatch(
        setPopAlert({
          alertType: 'success',
          message: 'Clientes actualizados correctamente',
          show: true,
        })
      );
      getCustomers();
    } else {
      dispatch(
        setPopAlert({
          alertType: 'error',
          message: 'Error al actualizar los clientes',
          show: true,
        })
      );
    }
  };

  const handleRefreshAlegra = async () => {
    dispatch(setLoading(true));
    const response = await apiCall(
      'GET',
      'customers/refresh-alegra/?all=1&page_size=100',
      null,
      '',
      'Error al obtener los clientes'
    );
    dispatch(setLoading(false));
    if (response) {
      dispatch(
        setPopAlert({
          alertType: 'success',
          message: `Alegra: ${response.created || 0} importados, ${response.updated || 0} actualizados, ${response.fetched || response.count_in_page || 0} en total`,
          show: true,
        })
      );
      getCustomers();
      if (response.duplicates?.length) {
        setDuplicates(response.duplicates);
        setShowDuplicates(true);
        setSelectedDuplicateKeys({});
      }
    } else {
      dispatch(
        setPopAlert({
          alertType: 'error',
          message: 'Error al actualizar los clientes desde Alegra',
          show: true,
        })
      );
    }
  };

  const handleResolveDuplicate = async (dup: CustomerDuplicateType) => {
    if (!dup.can_update || !dup.suggested_updates) {
      return;
    }
    const confirmed = window.confirm('Deseas actualizar la informacion del cliente con los datos de Alegra?');
    if (!confirmed) {
      return;
    }
    dispatch(setLoading(true));
    const response = await resolveAlegraDuplicate({
      customer_id: dup.siigo_customer.id,
      alegra_id: dup.alegra_customer.id,
      updates: dup.suggested_updates || {},
    });
    dispatch(setLoading(false));
    if (response.ok) {
      dispatch(
        setPopAlert({
          alertType: 'success',
          message: 'Cliente actualizado con datos de Alegra',
          show: true,
        })
      );
      const resolvedKey = `${dup.siigo_customer.id}-${dup.alegra_customer.id}`;
      setDuplicates((prev) =>
        prev.map((item) => {
          const key = `${item.siigo_customer.id}-${item.alegra_customer.id}`;
          if (key !== resolvedKey) {
            return item;
          }
          return mergeDupSources(item, 'alegra');
        })
      );
      setSelectedDuplicateKeys((prev) => ({ ...prev, [resolvedKey]: false }));
      getCustomers();
    }
    if (!response.ok) {
      dispatch(
        setPopAlert({
          alertType: 'warning',
          message: response.message || 'No se pudo actualizar el cliente',
          show: true,
        })
      );
    }
  };

  const toggleDuplicateSelection = (key: string, checked: boolean) => {
    setSelectedDuplicateKeys((prev) => ({ ...prev, [key]: checked }));
  };

  const resolveFieldSource = (dup: CustomerDuplicateType, field: string) => {
    const sources = dup.siigo_customer.sync_sources || {};
    if (sources[field]) {
      return sources[field];
    }
    if (!dup.siigo_customer.siigo_id) {
      return 'manual';
    }
    return 'siigo';
  };

  const getDupStatus = (dup: CustomerDuplicateType) => {
    const fields = Object.keys(dup.differences || {});
    const sources = fields.map((field) => resolveFieldSource(dup, field)).filter(Boolean);
    if (!sources.length) {
      return resolveFieldSource(dup, 'customer_name');
    }
    if (sources.every((source) => source === sources[0])) {
      return sources[0];
    }
    return 'mixed';
  };

  const mergeDupSources = (dup: CustomerDuplicateType, source: string) => {
    const fields = dup.suggested_fields || Object.keys(dup.suggested_updates || {});
    const nextSources = { ...(dup.siigo_customer.sync_sources || {}) };
    fields.forEach((field) => {
      nextSources[field] = source;
    });
    return {
      ...dup,
      siigo_customer: {
        ...dup.siigo_customer,
        sync_sources: nextSources,
      },
    };
  };

  const toggleSelectAllDuplicates = (checked: boolean) => {
    const next: Record<string, boolean> = {};
    duplicates.forEach((dup) => {
      const key = `${dup.siigo_customer.id}-${dup.alegra_customer.id}`;
      const status = getDupStatus(dup);
      if (dup.can_update && status !== 'alegra') {
        next[key] = checked;
      }
    });
    setSelectedDuplicateKeys(next);
  };

  const handleResolveSelectedDuplicates = async () => {
    const selected = duplicates.filter((dup) => {
      const key = `${dup.siigo_customer.id}-${dup.alegra_customer.id}`;
      const status = getDupStatus(dup);
      return dup.can_update && status !== 'alegra' && selectedDuplicateKeys[key];
    });
    if (!selected.length) {
      return;
    }
    const confirmed = window.confirm('Deseas actualizar los clientes seleccionados con los datos de Alegra?');
    if (!confirmed) {
      return;
    }
    dispatch(setLoading(true));
    let updatedCount = 0;
    let errorCount = 0;
    for (const dup of selected) {
      const response = await resolveAlegraDuplicate({
        customer_id: dup.siigo_customer.id,
        alegra_id: dup.alegra_customer.id,
        updates: dup.suggested_updates || {},
      });
      if (response.ok) {
        updatedCount += 1;
      } else {
        errorCount += 1;
        dispatch(
          setPopAlert({
            alertType: 'warning',
            message: response.message || 'No se pudo actualizar el cliente',
            show: true,
          })
        );
      }
    }
    dispatch(setLoading(false));
    if (updatedCount) {
      dispatch(
        setPopAlert({
          alertType: 'success',
          message: `Clientes actualizados: ${updatedCount}${errorCount ? ` (conflictos: ${errorCount})` : ''}`,
          show: true,
        })
      );
      const resolvedKeys = new Set(selected.map((dup) => `${dup.siigo_customer.id}-${dup.alegra_customer.id}`));
      setDuplicates((prev) =>
        prev.map((dup) => {
          const key = `${dup.siigo_customer.id}-${dup.alegra_customer.id}`;
          if (!resolvedKeys.has(key)) {
            return dup;
          }
          return mergeDupSources(dup, 'alegra');
        })
      );
      setSelectedDuplicateKeys((prev) => {
        const next = { ...prev };
        resolvedKeys.forEach((key) => {
          next[key] = false;
        });
        return next;
      });
      getCustomers();
    } else {
      dispatch(
        setPopAlert({
          alertType: 'warning',
          message: errorCount ? `No se actualizo ningun cliente (conflictos: ${errorCount})` : 'No se actualizo ningun cliente',
          show: true,
        })
      );
    }
  };

  const selectedDuplicatesCount = duplicates.reduce((count, dup) => {
    const key = `${dup.siigo_customer.id}-${dup.alegra_customer.id}`;
    const status = getDupStatus(dup);
    if (dup.can_update && status !== 'alegra' && selectedDuplicateKeys[key]) {
      return count + 1;
    }
    return count;
  }, 0);

  const allResolved =
    duplicates.length > 0 &&
    duplicates.every((dup) => {
      return getDupStatus(dup) === 'alegra';
    });

  const handleManualChange = (field: string, value: string | boolean) => {
    setManualForm((prev) => ({ ...prev, [field]: value }));
  };

  const handleCreateCity = async () => {
    dispatch(setLoading(true));
    const response = await apiCall('POST', 'cities/', cityForm, '', 'No se pudo crear la ciudad');
    dispatch(setLoading(false));
    if (response) {
      setCities((prev) => {
        if (prev.some((city) => city.id === response.id)) {
          return prev;
        }
        return [...prev, response];
      });
      setManualForm((prev) => ({ ...prev, cityId: response.id.toString() }));
      setCityForm({ city_name: '', state_name: '', country_name: '' });
      setShowCityModal(false);
    }
  };

  const handleCreateManualCustomer = async () => {
    const customerName = `${manualForm.firstName} ${manualForm.lastName}`.trim();
    if (!customerName || !manualForm.cityId) {
      dispatch(
        setPopAlert({
          alertType: 'error',
          message: 'Nombre y ciudad son obligatorios',
          show: true,
        })
      );
      return;
    }
    const payload = {
      customer_name: customerName,
      type: 'Customer',
      person_type: manualForm.personType,
      id_type: {
        code: manualForm.idTypeCode,
        name: manualForm.idTypeName,
      },
      identification: manualForm.identification,
      branch_office: Number(manualForm.branchOffice) || 0,
      check_digit: manualForm.checkDigit,
      name: [manualForm.firstName, manualForm.lastName].filter(Boolean),
      commercial_name: manualForm.commercialName,
      active: manualForm.active,
      vat_responsible: manualForm.vatResponsible,
      fiscal_responsibilities: [
        {
          code: manualForm.fiscalCode,
          name: manualForm.fiscalName,
        },
      ],
      address: {
        address: manualForm.address,
      },
      phones: manualForm.phone
        ? [
            {
              indicative: manualForm.phoneIndicative,
              number: manualForm.phone,
              extension: manualForm.phoneExtension,
            },
          ]
        : [],
      contacts: [
        {
          first_name: manualForm.contactFirstName || manualForm.firstName,
          last_name: manualForm.contactLastName || manualForm.lastName,
          email: manualForm.email,
          phone: manualForm.contactPhone
            ? {
                indicative: manualForm.contactIndicative,
                number: manualForm.contactPhone,
              }
            : {},
        },
      ],
      city_id: manualForm.cityId,
    };

    dispatch(setLoading(true));
    const response = await apiCall('POST', 'customers/manual/', payload, '', 'No se pudo crear el cliente');
    dispatch(setLoading(false));
    if (response) {
      dispatch(
        setPopAlert({
          alertType: 'success',
          message: 'Cliente creado correctamente',
          show: true,
        })
      );
      setShowManualModal(false);
      getCustomers();
    } else {
      dispatch(
        setPopAlert({
          alertType: 'error',
          message: 'No se pudo crear el cliente',
          show: true,
        })
      );
    }
  };

  const handlePageChange = (newPage: number) => {
    dispatch(setCurrentPage(newPage));
    // Actualizar URL con la nueva página
    const searchParams = new URLSearchParams(location.search);
    searchParams.set('page', newPage.toString());
    searchParams.set('rows', rowsPerPage.toString());
    navigate(`/clientes?${searchParams.toString()}`, { replace: true });
  };

  const handleRowsPerPageChange = (newRowsPerPage: number) => {
    dispatch(setRowsPerPage(newRowsPerPage));
    dispatch(setCurrentPage(1)); // Reset a página 1 cuando cambian las filas por página
    // Actualizar URL
    const searchParams = new URLSearchParams(location.search);
    searchParams.set('page', '1');
    searchParams.set('rows', newRowsPerPage.toString());
    navigate(`/clientes?${searchParams.toString()}`, { replace: true });
  };

  const columns = [
    { title: 'ID', field: 'id' },
    { title: 'Siigo ID', field: 'siigo_id' },
    { title: 'Alegra ID', field: 'alegra_id' },
    { title: 'Nombre', field: 'customer_name' },
    { title: 'Identificación', field: 'identification' },
    { title: 'Ciudad', field: 'customer_city.city_name' },
    { title: 'Estado', field: 'customer_city.state_name' },
    {
      title: 'Sincronizado',
      field: 'is_synced',
      type: 'decorator_bool',
      decoratorOptions: {
        activeLabel: 'Sí',
        inactiveLabel: 'No',
        activeColor: 'success' as const,
        inactiveColor: 'default' as const,
        activeStyle: { backgroundColor: '#b8e0c9', color: '#1f3d2b' },
        inactiveStyle: { backgroundColor: '#e5e7eb', color: '#374151' },
      },
    },
    { title: 'Ultima sincronizacion', field: 'last_synced_at', type: 'datetime' },
  ];

  const actions: Array<any> = [
    ...(siigoConfigured && canAdd_customer
      ? [
          {
            icon: (
              <span style={{ position: 'relative', display: 'inline-flex', alignItems: 'center' }}>
                <FontAwesomeIcon icon={faSync} />
                <span
                  style={{
                    position: 'absolute',
                    top: '-12px',
                    right: '-6px',
                    background: '#111827',
                    color: '#fff',
                    fontSize: '9px',
                    padding: '1px 4px',
                    borderRadius: '6px',
                    lineHeight: 1,
                  }}
                >
                  Siigo
                </span>
              </span>
            ),
            tooltip: 'Refrescar',
            condition: { field: 'siigo_id', operator: '>', value: '' },
            isFreeAction: true,
            onClick: (rowData: CustomerType) => handleGetCustomer(rowData.id.toString(), 'siigo'),
          },
        ]
      : []),
    ...(alegraConfigured && canAdd_customer
      ? [
          {
            icon: (
              <span style={{ position: 'relative', display: 'inline-flex', alignItems: 'center' }}>
                <FontAwesomeIcon icon={faSync} />
                <span
                  style={{
                    position: 'absolute',
                    top: '-12px',
                    right: '-6px',
                    background: '#111827',
                    color: '#fff',
                    fontSize: '9px',
                    padding: '1px 4px',
                    borderRadius: '6px',
                    lineHeight: 1,
                  }}
                >
                  Alegra
                </span>
              </span>
            ),
            tooltip: 'Refrescar desde Alegra',
            condition: { field: 'alegra_id', operator: '>', value: '' },
            isFreeAction: true,
            onClick: (rowData: CustomerType) => handleGetCustomer(rowData.id.toString(), 'alegra'),
          },
        ]
      : []),
    canView_customer
      ? {
          icon: <FontAwesomeIcon icon={faEye} />,
          tooltip: (row: CustomerType) =>
            (!siigoConfigured && row.siigo_id && !row.is_synced) ||
            (!alegraConfigured && row.alegra_id && !row.is_synced)
              ? 'Cliente externo, agregue datos de integracion en configuraciones'
              : 'Ver',
          disabled: (row: CustomerType) =>
            (!siigoConfigured && !!row.siigo_id && !row.is_synced) ||
            (!alegraConfigured && !!row.alegra_id && !row.is_synced),
          onClick: (rowData: CustomerType) => navigate(`/cliente/${rowData.id}`),
        }
      : null,
  ];

  return (
    <div className='container-wrap'>
      <Grid container spacing={2}>
        <Grid item xs={12} sm={6} className='text-left'>
          <h2 className='text-left'>Clientes</h2>
        </Grid>

        <Grid item xs={12} sm={2} className='text-right'>
          {siigoConfigured && canAdd_customer && (
            <Button className='btn btn-primary' onClick={handleRefreshClients}>
              <FontAwesomeIcon icon={faRefresh} spin /> Actualizar desde Siigo
            </Button>
          )}
          </Grid><Grid item xs={12} sm={2} className='text-right'>
          {alegraConfigured && canAdd_customer && (
            <Button className='btn btn-primary' onClick={handleRefreshAlegra}>
              <FontAwesomeIcon icon={faRefresh} spin /> Actualizar desde Alegra
            </Button>
          )}
        </Grid>

        <Grid item xs={12} sm={2} className='text-right'>
          {canAdd_customer && (
            <Button className='btn btn-primary' onClick={() => setShowManualModal(true)}>
              Crear cliente manual
            </Button>
          )}
        </Grid>
      </Grid>
      <DataTable
        data={customers}
        columns={columns}
        actions={actions}
        initialPage={currentPage}
        initialRowsPerPage={rowsPerPage}
        onPageChange={handlePageChange}
        onRowsPerPageChange={handleRowsPerPageChange}
      />
      <Dialog open={showDuplicates} onClose={() => setShowDuplicates(false)} fullWidth maxWidth='md'>
        <DialogTitle>Clientes duplicados con Siigo</DialogTitle>
        <DialogContent>
          {duplicates.length === 0 && <p>No se detectaron duplicados.</p>}
          {allResolved && <p>Todos los duplicados fueron resueltos.</p>}
          {duplicates.length > 0 && (
            <div style={{ marginBottom: '12px' }}>
              <label>
                <input
                  type='checkbox'
                  checked={
                    duplicates.filter((dup) => {
                      return dup.can_update && getDupStatus(dup) !== 'alegra';
                    }).length > 0 &&
                    duplicates.filter((dup) => {
                      return dup.can_update && getDupStatus(dup) !== 'alegra';
                    }).every((dup) => {
                      const key = `${dup.siigo_customer.id}-${dup.alegra_customer.id}`;
                      return !!selectedDuplicateKeys[key];
                    })
                  }
                  onChange={(event) => toggleSelectAllDuplicates(event.target.checked)}
                />
                Seleccionar todos
              </label>
              <Button
                className='btn btn-primary'
                onClick={handleResolveSelectedDuplicates}
                disabled={selectedDuplicatesCount === 0}
              >
                Actualizar seleccionados ({selectedDuplicatesCount})
              </Button>
            </div>
          )}
          {duplicates.map((dup) => (
            <div
              key={`${dup.siigo_customer.id}-${dup.alegra_customer.id}`}
              style={{
                border: '1px solid #e5e7eb',
                borderRadius: '8px',
                padding: '12px',
                marginBottom: '12px',
                background: '#fff',
                boxShadow: '0 1px 2px rgba(0,0,0,0.04)',
              }}
            >
              <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                {dup.can_update && getDupStatus(dup) !== 'alegra' && (
                  <label style={{ marginRight: '4px' }}>
                    <input
                      type='checkbox'
                      checked={!!selectedDuplicateKeys[`${dup.siigo_customer.id}-${dup.alegra_customer.id}`]}
                      onChange={(event) =>
                        toggleDuplicateSelection(`${dup.siigo_customer.id}-${dup.alegra_customer.id}`, event.target.checked)
                      }
                    />
                  </label>
                )}
                <strong>{dup.siigo_customer.customer_name}</strong>
              </div>
              <div style={{ marginTop: '6px', fontWeight: 600 }}>
                {getDupStatus(dup) === 'alegra' && 'Actualmente con los datos de Alegra'}
                {getDupStatus(dup) === 'siigo' && 'Actualmente con los datos de Siigo'}
                {getDupStatus(dup) === 'manual' && 'Actualmente con los datos Manuales'}
                {getDupStatus(dup) === 'mixed' && 'Actualmente con datos mixtos'}
              </div>
              <div style={{ marginTop: '4px' }}>
                Siigo ID: {dup.siigo_customer.siigo_id || '-'} | Alegra ID: {dup.alegra_customer.id}
              </div>
              {dup.conflict && (
                <div style={{ color: '#b45309' }}>
                  Conflicto: Alegra ID ya asociado, es posible que este cliente esté duplicado en la plataforma de alegra, no es error de Jarvis
                </div>
              )}
              {dup.match_reason === 'name' && (
                <div style={{ color: '#2563eb' }}>Coincidencia por nombre (sin identificación)</div>
              )}
                {Object.keys(dup.differences || {}).length > 0 && (
                  <div style={{ marginTop: '8px' }}>
                    <strong>Cambios detectados</strong>
                    {Object.entries(dup.differences).map(([field, diff]) => (
                      <div key={field} style={{ marginTop: '6px' }}>
                        <div style={{ fontWeight: 600 }}>{FIELD_LABELS[field] || 'Campo'}</div>
                        <div>Datos actuales: {diff.current || '-'}</div>
                        <div>Nuevos datos: {diff.updated || '-'}</div>
                      </div>
                    ))}
                  </div>
                )}
              {dup.can_update && getDupStatus(dup) !== 'alegra' && (
                <Button className='btn btn-secondary' onClick={() => handleResolveDuplicate(dup)}>
                  Actualizar con Alegra
                </Button>
              )}
            </div>
          ))}
        </DialogContent>
        <DialogActions>
          <Button className='btn btn-primary' onClick={() => setShowDuplicates(false)}>
            Cerrar
          </Button>
        </DialogActions>
      </Dialog>
      <Dialog open={showManualModal} onClose={() => setShowManualModal(false)} fullWidth maxWidth='md'>
        <DialogTitle>Crear cliente manual</DialogTitle>
        <DialogContent>
          <Grid container spacing={2} sx={{ mt: 1 }}>
            <Grid item xs={12} sm={6}>
              <TextField
                label='Nombre'
                value={manualForm.firstName}
                onChange={(e) => handleManualChange('firstName', e.target.value)}
                fullWidth
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                label='Apellido'
                value={manualForm.lastName}
                onChange={(e) => handleManualChange('lastName', e.target.value)}
                fullWidth
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                label='Tipo de persona'
                value={manualForm.personType}
                onChange={(e) => handleManualChange('personType', e.target.value)}
                fullWidth
              />
            </Grid>
            <Grid item xs={12} sm={3}>
              <TextField
                label='Tipo ID (código)'
                value={manualForm.idTypeCode}
                onChange={(e) => handleManualChange('idTypeCode', e.target.value)}
                fullWidth
              />
            </Grid>
            <Grid item xs={12} sm={3}>
              <TextField
                label='Tipo ID (nombre)'
                value={manualForm.idTypeName}
                onChange={(e) => handleManualChange('idTypeName', e.target.value)}
                fullWidth
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                label='Identificación'
                value={manualForm.identification}
                onChange={(e) => handleManualChange('identification', e.target.value)}
                fullWidth
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                label='Dígito de verificación'
                value={manualForm.checkDigit}
                onChange={(e) => handleManualChange('checkDigit', e.target.value)}
                fullWidth
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                label='Nombre comercial'
                value={manualForm.commercialName}
                onChange={(e) => handleManualChange('commercialName', e.target.value)}
                fullWidth
              />
            </Grid>
            <Grid item xs={12} sm={3}>
              <TextField
                label='Sucursal'
                value={manualForm.branchOffice}
                onChange={(e) => handleManualChange('branchOffice', e.target.value)}
                type='number'
                fullWidth
              />
            </Grid>
            <Grid item xs={12} sm={3}>
              <FormControlLabel
                control={
                  <Switch
                    checked={manualForm.active}
                    onChange={(e) => handleManualChange('active', e.target.checked)}
                  />
                }
                label='Activo'
              />
            </Grid>
            <Grid item xs={12}>
              <TextField
                label='Dirección'
                value={manualForm.address}
                onChange={(e) => handleManualChange('address', e.target.value)}
                fullWidth
              />
            </Grid>
            <Grid item xs={12} sm={8}>
              <TextField
                select
                label='Ciudad'
                value={manualForm.cityId}
                onChange={(e) => handleManualChange('cityId', e.target.value)}
                fullWidth
                SelectProps={{ native: true }}
              >
                <option value=''>Selecciona una ciudad</option>
                {cities.map((city) => (
                  <option key={city.id} value={city.id}>
                    {city.city_name} - {city.state_name || ''} - {city.country_name || ''}
                  </option>
                ))}
              </TextField>
            </Grid>
            <Grid item xs={12} sm={4} className='text-right'>
              {canAdd_city && (
                <Button className='btn btn-secondary' onClick={() => setShowCityModal(true)}>
                  Crear ciudad
                </Button>
              )}
            </Grid>
            <Grid item xs={12} sm={4}>
              <TextField
                label='Indicativo teléfono'
                value={manualForm.phoneIndicative}
                onChange={(e) => handleManualChange('phoneIndicative', e.target.value)}
                fullWidth
              />
            </Grid>
            <Grid item xs={12} sm={4}>
              <TextField
                label='Teléfono'
                value={manualForm.phone}
                onChange={(e) => handleManualChange('phone', e.target.value)}
                fullWidth
              />
            </Grid>
            <Grid item xs={12} sm={4}>
              <TextField
                label='Extensión'
                value={manualForm.phoneExtension}
                onChange={(e) => handleManualChange('phoneExtension', e.target.value)}
                fullWidth
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                label='Email'
                value={manualForm.email}
                onChange={(e) => handleManualChange('email', e.target.value)}
                fullWidth
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <FormControlLabel
                control={
                  <Switch
                    checked={manualForm.vatResponsible}
                    onChange={(e) => handleManualChange('vatResponsible', e.target.checked)}
                  />
                }
                label='Responsable de IVA'
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                label='Contacto nombre'
                value={manualForm.contactFirstName}
                onChange={(e) => handleManualChange('contactFirstName', e.target.value)}
                fullWidth
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                label='Contacto apellido'
                value={manualForm.contactLastName}
                onChange={(e) => handleManualChange('contactLastName', e.target.value)}
                fullWidth
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                label='Contacto teléfono'
                value={manualForm.contactPhone}
                onChange={(e) => handleManualChange('contactPhone', e.target.value)}
                fullWidth
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                label='Indicativo contacto'
                value={manualForm.contactIndicative}
                onChange={(e) => handleManualChange('contactIndicative', e.target.value)}
                fullWidth
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                label='Responsabilidad fiscal (código)'
                value={manualForm.fiscalCode}
                onChange={(e) => handleManualChange('fiscalCode', e.target.value)}
                fullWidth
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                label='Responsabilidad fiscal (nombre)'
                value={manualForm.fiscalName}
                onChange={(e) => handleManualChange('fiscalName', e.target.value)}
                fullWidth
              />
            </Grid>
          </Grid>
        </DialogContent>
        <DialogActions>
          <Button className='btn btn-secondary' onClick={() => setShowManualModal(false)}>
            Cancelar
          </Button>
          {canAdd_customer && (
            <Button className='btn btn-primary' onClick={handleCreateManualCustomer}>
              Guardar cliente
            </Button>
          )}
        </DialogActions>
      </Dialog>
      <Dialog open={showCityModal} onClose={() => setShowCityModal(false)} fullWidth maxWidth='sm'>
        <DialogTitle>Crear ciudad</DialogTitle>
        <DialogContent>
          <Grid container spacing={2} sx={{ mt: 1 }}>
            <Grid item xs={12}>
              <TextField
                label='Ciudad'
                value={cityForm.city_name}
                onChange={(e) => setCityForm((prev) => ({ ...prev, city_name: e.target.value }))}
                fullWidth
              />
            </Grid>
            <Grid item xs={12}>
              <TextField
                label='Departamento'
                value={cityForm.state_name}
                onChange={(e) => setCityForm((prev) => ({ ...prev, state_name: e.target.value }))}
                fullWidth
              />
            </Grid>
            <Grid item xs={12}>
              <TextField
                label='País'
                value={cityForm.country_name}
                onChange={(e) => setCityForm((prev) => ({ ...prev, country_name: e.target.value }))}
                fullWidth
              />
            </Grid>
          </Grid>
        </DialogContent>
        <DialogActions>
          <Button className='btn btn-secondary' onClick={() => setShowCityModal(false)}>
            Cancelar
          </Button>
          {canAdd_city && (
            <Button className='btn btn-primary' onClick={handleCreateCity}>
              Crear ciudad
            </Button>
          )}
        </DialogActions>
      </Dialog>
    </div>
  );
};

export default ViewCustomersList;
